import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  InputLabel,
  Grid,
} from "@mui/material";
import { useStyle } from "../../../HR/Pages/Attendance/attendance-styles";
import Modal from "../../../../Components/Modal/Modal";
import { useDispatch, useSelector } from "react-redux";
import { Form, Spinner } from "react-bootstrap";
import {
  createAsset,
  getAllAssetCategories,
} from "../../../../Redux/Actions/assetActions";
import { errorNotify, successNotify } from "../../../../Utils/toast";
import { clearErrors } from "../../../../Redux/Actions/Auth";
import { BsFillFileImageFill } from "react-icons/bs";

const AddAssetModal = ({
  open,
  onClose,
  categories = [],
  categoriesLoading = false,
  onAssetCreated
}) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  // Use asset slice for create asset state
  const { createAssetLoading, createAssetError, createAssetSuccess } =
    useSelector((state) => state.asset);

  const [formData, setFormData] = useState({
    tag: "",
    category: "",
    brand: "",
    model: "",
    date_of_purchase: "",
    price: "",
    date_of_warranty: "",
    condition: "new",
  });
  const [docs, setDocs] = useState([]);
  const [previewDocs, setPreviewDocs] = useState([]);
  const [extDocs, setExtDocs] = useState([]);

  // Handle success/failure for asset creation
  useEffect(() => {
    if (createAssetSuccess) {
      successNotify("Asset created successfully!");
      handleClose();
      dispatch({ type: "CREATE_ASSET_RESET" });
      if (typeof onAssetCreated === 'function') {
        onAssetCreated();
      }
    }
    if (createAssetError) {
      errorNotify(createAssetError);
      // Don't close modal
    }
    // eslint-disable-next-line
  }, [createAssetSuccess, createAssetError, onAssetCreated]);

  const resetForm = () => {
    setFormData({
      tag: "",
      category: "",
      brand: "",
      model: "",
      date_of_purchase: "",
      price: "",
      date_of_warranty: "",
      condition: "new",
    });
    setDocs([]);
    setPreviewDocs([]);
    setExtDocs([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadDocsChange = (e) => {
    const files = Array.from(e.target.files);
    Promise.all(files.map((file) => URL.createObjectURL(file))).then((urls) => {
      setPreviewDocs(urls);
    });
    Promise.all(files.map((file) => file.name)).then((ext) => {
      setExtDocs(ext);
    });
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          setDocs((old) => [...old, reader.result]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tag || !formData.category) {
      errorNotify("Please fill in all required fields");
      return;
    }
    const flattenedDocs = Array.isArray(docs)
      ? docs.flat
        ? docs.flat(Infinity)
        : docs.reduce((acc, v) => acc.concat(v), [])
      : [];
    const payload = {
      ...formData,
      asset_docs: flattenedDocs,
    };
    dispatch(createAsset(payload));
    // Do not close modal here; wait for success
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Modal open={open} setOpen={onClose} title="Add Asset" size="600">
      <React.Fragment>
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <InputLabel>Tag *</InputLabel>
              <TextField
                name="tag"
                value={formData.tag}
                onChange={handleInputChange}
                fullWidth
                required
                sx={{ mb: 2 }}
                placeholder="Enter asset tag"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Category *</InputLabel>
              <TextField
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                select
                fullWidth
                required
                sx={{ mb: 2 }}
                placeholder="Select category"
                disabled={categoriesLoading}
              >
                <MenuItem value="" disabled>
                  {categoriesLoading ? "Loading..." : "Select category"}
                </MenuItem>
                {categories &&
                  categories.length > 0 &&
                  categories.map((cat) => {
                    // Capitalize first letter of each word
                    const displayName = cat.name.replace(
                      /\w\S*/g,
                      (txt) =>
                        txt.charAt(0).toUpperCase() +
                        txt.substr(1).toLowerCase()
                    );
                    return (
                      <MenuItem key={cat._id} value={cat._id}>
                        {displayName}
                      </MenuItem>
                    );
                  })}
                {!categoriesLoading &&
                  (!categories || categories.length === 0) && (
                    <MenuItem value="" disabled>
                      No categories found
                    </MenuItem>
                  )}
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Brand</InputLabel>
              <TextField
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="Enter brand"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Model</InputLabel>
              <TextField
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="Enter model"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Date of Purchase</InputLabel>
              <TextField
                name="date_of_purchase"
                value={formData.date_of_purchase}
                onChange={handleInputChange}
                type="date"
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Price</InputLabel>
              <TextField
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                type="number"
                fullWidth
                sx={{ mb: 2 }}
                placeholder="Enter price"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Date of Warranty</InputLabel>
              <TextField
                name="date_of_warranty"
                value={formData.date_of_warranty}
                onChange={handleInputChange}
                type="date"
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel>Condition</InputLabel>
              <TextField
                name="condition"
                value={formData.condition}
                onChange={handleInputChange}
                select
                fullWidth
                sx={{ mb: 2 }}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="used">Used</MenuItem>
                <MenuItem value="damaged">Damaged</MenuItem>
                <MenuItem value="repaired">Repaired</MenuItem>
              </TextField>
            </Grid>
            <Grid
              item
              md={12}
              xs={12}
              sx={{ display: "flex", gap: "25px", mt: 2, flexWrap: "wrap" }}
            >
              {previewDocs.length !== 0 &&
                previewDocs.map((d, i) => {
                  return (
                    <div
                      key={i}
                      style={{
                        width: "90px",
                        height: "120px",
                        border: "1px solid lightgrey",
                        padding: "5px",
                      }}
                    >
                      {extDocs[i]?.includes(".pdf") && (
                        <iframe src={d} width="100%" height="100px" />
                      )}
                      {(extDocs[i]?.includes(".png") ||
                        extDocs[i]?.includes(".jpg") ||
                        extDocs[i]?.includes(".jpeg")) && (
                        <img
                          style={{
                            width: "100%",
                            objectFit: "cover",
                            height: "100px",
                          }}
                          src={d}
                          alt="doc"
                        />
                      )}
                    </div>
                  );
                })}
            </Grid>
            <Grid item md={12} xs={12} sx={{ mt: 3 }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "20px" }}
              >
                <Button variant="contained" component="label">
                  Upload Documents
                  <BsFillFileImageFill
                    style={{ fontSize: "26px", marginLeft: "10px" }}
                  />
                  <input
                    hidden
                    accept="image/*, .pdf"
                    multiple
                    type="file"
                    onChange={uploadDocsChange}
                  />
                </Button>
                <p style={{ marginBottom: "0px", color: "red" }}>
                  *Supported files: Images and pdf only.
                </p>
              </div>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                variant="contained"
                type="button"
                onClick={handleClose}
                className={classes.cancelBtn}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={createAssetLoading}
                className={classes.createLeaveBtn}
              >
                {createAssetLoading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "Add Asset"
                )}
              </Button>
            </Box>
          </Box>
        </Form>
      </React.Fragment>
    </Modal>
  );
};

export default AddAssetModal;
